<template>
  <HeaderBar />

  <ButtonBackContainer v-if="!isLastStep">
    <ButtonBack :to="backRoute">{{ t('back') }}</ButtonBack>
    <CVButton outline variant="error" @click="onCancelClicked">
      {{ t('cancelReturn') }}
      <XMarkIcon class="h-4 w-4" />
    </CVButton>
  </ButtonBackContainer>

  <section class="carvia-container flex flex-col justify-center">
    <router-view />
  </section>
</template>

<script setup lang="ts">
import { ProcessStep } from '@carvia/ros-client-types';
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import { useI18n } from 'vue-i18n';
import { useBooking } from '@/queries/use-bookings';
import { useUiStore } from '@/stores/ui.store';
import { computed, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import { Alert } from '@/services/alert.service';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import {
  useProcessSessionTracking,
  type ProcessStepDefinition,
} from '@/hooks/use-process-step-tracking';
import { ProcessSessionType } from '@carvia/ros-client-types';
import { useFeatureFlagSnapshot } from '@/hooks/use-feature-flag';
import { VEHICLE_INSPECTION_FLAG } from '@/components/inspections/inspection-state';

const uiStore = useUiStore();
const { t } = useI18n();
const route = useRoute();
const { data: booking } = useBooking(route.params.id as string);

const inspectionFlag = useFeatureFlagSnapshot(VEHICLE_INSPECTION_FLAG);

const steps = computed<ProcessStepDefinition[]>(() => [
  { processStep: ProcessStep.RETURN_OVERVIEW, routeName: 'returnOverview' },
  { processStep: ProcessStep.RETURN_DAMAGES, routeName: 'returnDamages' },
  // Photos come before car status: car status completes the return and locks the inspection.
  ...(inspectionFlag.isEnabled.value
    ? [{ processStep: ProcessStep.RETURN_INSPECTION, routeName: 'returnInspection' }]
    : []),
  { processStep: ProcessStep.RETURN_CAR_STATUS, routeName: 'returnCarStatus' },
  { processStep: null, routeName: 'returnDone' },
]);

// The photo step depends on the flag, so the steps are known once PostHog has loaded it.
const stepsReady = computed(() => inspectionFlag.isLoaded.value);

const { clearProcessSession, backRoute, navigateToList, isLastStep } = useProcessSessionTracking({
  booking,
  steps,
  listRouteName: 'upcomingHandovers',
  stepsReady,
  sessionType: ProcessSessionType.RETURN,
});

watchEffect(() => {
  if (booking.value) {
    const { customerData, allocations } = booking.value;
    const vehicleType = allocations[allocations.length - 1]?.car.vehicleType;
    uiStore.setHeaderTitle(
      t('return'),
      `${customerData?.firstName ?? 'n/a'} ${customerData?.lastName ?? 'n/a'}`,
      `${vehicleType?.make.name} ${vehicleType?.model}`,
    );
  } else {
    uiStore.setHeaderTitle(t('return'));
  }
});

const onCancelClicked = async () => {
  const result = await Alert.fire<string, 'resetAndLeave' | 'justLeave' | 'stay'>({
    title: t('cancelReturn'),
    text: t('cancelReturnConfirmation'),
    icon: 'warning',
    buttons: [
      { propertyName: 'resetAndLeave', text: t('resetAndLeave'), style: 'danger' },
      { propertyName: 'justLeave', text: t('justLeave'), style: 'secondary' },
      { propertyName: 'stay', text: t('stay'), style: 'ghost' },
    ],
  });

  if (result.resetAndLeave) {
    await clearProcessSession();
    await navigateToList();
  } else if (result.justLeave) {
    await navigateToList({ preserveSession: true });
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "return": "Return",
    "back": "Back",
    "cancelReturn": "Cancel return",
    "cancelReturnConfirmation": "Do you really want to cancel the return?",
    "resetAndLeave": "Yes, reset progress",
    "justLeave": "Just go back",
    "stay": "Stay here"
  },
  "de": {
    "return": "Rückgabe",
    "back": "Zurück",
    "cancelReturn": "Rückgabe abbrechen",
    "cancelReturnConfirmation": "Möchtest du die Rückgabe wirklich abbrechen?",
    "resetAndLeave": "Ja, Fortschritt zurücksetzen",
    "justLeave": "Nur zurück",
    "stay": "Hier bleiben"
  },
  "es": {
    "return": "Devolución",
    "back": "Volver",
    "cancelReturn": "Cancelar devolución",
    "cancelReturnConfirmation": "¿Seguro que desea cancelar la devolución?",
    "resetAndLeave": "Sí, restablecer el progreso",
    "justLeave": "Solo volver atrás",
    "stay": "Permanecer aquí"
  }
}
</i18n>
